import { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { rsaEncrypt } from '@PetSureAustralia/petsure-payment/core';
import { BrandContext } from '../../../contextProviders/brandContext';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { PAYMENT_METHOD, PETSURE_UNDERWRITER } from '../../../constants/constants';
import {
  showSelectAllPetCheckbox,
  isPetsureUnderwriter,
  legacyPolicyNumberHandler
} from '../../../helpers/paymentHelper';
import { FORM_KEYS } from '../../../constants/formKeys';
import { SESSION_STORAGE_KEYS } from '../../../constants/sessionStorageKeys';
import { useUpdatePremiumPaymentDetailsMutation } from '../../../data/policy';
import { useUpdateAllPremiumPaymentDetailsMutation } from '../../../data/customer';
import { useRegisterAccountMutation } from '../../../data/premiumsandpayments';
import { useGetGeneralTooltipContentQuery } from '../../../data/kontentAi';
import { GENERAL_TOOLTIP_CODENAME } from '../../../constants/cmsCodeName';

export const usePremiumPaymentPopUp = (activePet, petDetails, onCancel, setShowSuccessMessage) => {
  const fieldFormContext = useFormContext();
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;
  const [paymentMethod, setPaymentMethod] = useState(0);
  const [showFailedUpdateError, setShowFailedUpdateError] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const ddRef = useRef();
  const [updatePremiumPaymentDetails] = useUpdatePremiumPaymentDetailsMutation();
  const [updateAllPremiumPaymentDetails] = useUpdateAllPremiumPaymentDetailsMutation();
  const [registerAccount] = useRegisterAccountMutation();
  const applyToAll = fieldFormContext.watch(FORM_KEYS.APPLY_TO_ALL_PETS);
  const { ACCOUNT_NAME, BSB, ACCOUNT_NUMBER } = FORM_KEYS;

  const { data: generalTooltip } = useGetGeneralTooltipContentQuery({
    previewEnabled: isKontentAiPreviewEnabled,
    element: [GENERAL_TOOLTIP_CODENAME.SOLE_AUTHORITY_TOOLTIP]
  });
  const soleAuthorityTooltip =
    generalTooltip?.content?.[GENERAL_TOOLTIP_CODENAME.SOLE_AUTHORITY_TOOLTIP]?.[
      GENERAL_TOOLTIP_CODENAME.SOLE_AUTHORITY_TOOLTIP
    ]?.value || '';

  const {
    activeBrandContext: {
      brand: { partner, partnerPrefix },
      cspConfiguration: {
        creditCardConfig: {
          [process.env.REACT_APP_ENV]: { formUrl, supplierBusinessCode }
        }
      }
    }
  } = useContext(BrandContext);
  // Checks if all pets have the same underwriter
  const showSelectAllCheckbox = showSelectAllPetCheckbox(petDetails);

  const PUBLIC_KEY = process.env.RSA_ENCRYPT_PUBLIC_KEY;

  const returnUrlState = useMemo(() => {
    const policyNumbers = applyToAll ? petDetails?.map((pet) => pet.PolicyNumber) : [activePet.PolicyNumber];

    const encryptUserState = {
      DigitalToken: sessionStorage?.getItem(SESSION_STORAGE_KEYS.CSP_ACCESS_TOKEN),
      PartnerNumber: partnerPrefix,
      PaymentMethod: PAYMENT_METHOD.CREDIT_CARD,
      UsePetsure: activePet.Underwriter === PETSURE_UNDERWRITER
    };

    const encryptedUserState = rsaEncrypt(JSON.stringify(encryptUserState), PUBLIC_KEY);
    const encryptedPolicyNumberState = rsaEncrypt(JSON.stringify({ PolicyNumbers: policyNumbers }), PUBLIC_KEY);

    return {
      userState: encodeURIComponent(encryptedUserState),
      policyState: encodeURIComponent(encryptedPolicyNumberState)
    };
  }, [activePet?.PolicyNumber, activePet?.Underwriter, petDetails, applyToAll, partnerPrefix, PUBLIC_KEY]);

  const isPetsureUw = useMemo(() => {
    if (!activePet || !petDetails) return false;
    return isPetsureUnderwriter({
      petDetails,
      policyNumber: activePet.PolicyNumber
    });
  }, [activePet, petDetails]);

  useEffect(() => {
    if (!updateSuccess) return;
    onCancel();
    fieldFormContext.reset();
    setShowSuccessMessage({
      active: true,
      isApplyToAllPetChecked: applyToAll
    });
  }, [updateSuccess, applyToAll, onCancel, setShowSuccessMessage, fieldFormContext]);

  const handleClose = () => {
    fieldFormContext.reset();
    onCancel();
  };

  const handlePaymentMethodChange = (value) => {
    setPaymentMethod(value);
  };

  const handleUpdateDirectDebit = async () => {
    if (activePet.Underwriter === PETSURE_UNDERWRITER) {
      const isValid = await fieldFormContext.trigger([ACCOUNT_NAME, BSB, ACCOUNT_NUMBER], {
        shouldFocus: true
      });

      if (isValid) {
        const { accountName, bsb, accountNumber, applyToAll } = fieldFormContext.getValues();
        // Remove any dashes from the 'bsb' value
        const bsbNumber = bsb.replace(/-/g, '');
        const isPetsureUnderwriter = activePet.Underwriter === PETSURE_UNDERWRITER;
        const premiumPayment = {
          AccountHoldeName: accountName,
          AccountNumber: accountNumber,
          Bank: '',
          BsbNumber: bsbNumber,
          CardExpiryDate: '',
          CardNumber: '',
          CardOwnerName: '',
          CardType: '',
          PartnerNumber: activePet.PartnerNumber,
          PaymentMethod: PAYMENT_METHOD.BANK,
          PolicyNumber: activePet.PolicyNumber,
          Token: 'D0'
        };
        if (applyToAll) {
          try {
            const response = await updateAllPremiumPaymentDetails({
              data: premiumPayment,
              isPetsureUnderwriter
            }).unwrap();

            if (response?.isSuccessResponse === false) {
              setShowFailedUpdateError(true);
              return;
            }

            setUpdateSuccess(true);
          } catch {
            setShowFailedUpdateError(true);
          }
        } else {
          try {
            const response = await updatePremiumPaymentDetails({
              data: premiumPayment,
              policyNumber: legacyPolicyNumberHandler(activePet.PolicyNumber),
              isPetsureUnderwriter
            }).unwrap();

            if (response?.isSuccessResponse === false) {
              setShowFailedUpdateError(true);
              return;
            }

            setUpdateSuccess(true);
          } catch {
            setShowFailedUpdateError(true);
          }
        }
      }
    } else {
      ddRef.current?.submitDirectDebit();
    }
  };

  const handlePaymentSuccess = async (result) => {
    try {
      const response = await registerAccount({
        singleUseToken: result.singleUseTokenId
      }).unwrap();

      const directDebitResp = response?.responseObject?.RegisterDirectDebitAccountResp;

      const { AccountToken, AccountName, Bsb, AccountNumber } = directDebitResp;

      const directDebitPayload = {
        AccountHoldeName: AccountName,
        AccountNumber,
        Bank: '',
        BsbNumber: Bsb.replace(/-/g, ''),
        CardExpiryDate: '',
        CardNumber: '',
        CardOwnerName: '',
        CardType: '',
        PartnerNumber: activePet.PartnerNumber,
        PaymentMethod: PAYMENT_METHOD.BANK,
        Token: AccountToken
      };

      const isPetsureUnderwriter = activePet.Underwriter === PETSURE_UNDERWRITER;

      if (applyToAll) {
        const responses = await Promise.all(
          petDetails.map((pet) =>
            updatePremiumPaymentDetails({
              data: {
                ...directDebitPayload,
                PolicyNumber: pet.PolicyNumber
              },
              policyNumber: legacyPolicyNumberHandler(pet.PolicyNumber),
              isPetsureUnderwriter
            })
              .unwrap()
              .catch(() => ({ isSuccessResponse: false }))
          )
        );

        if (responses.some((premiumResponse) => premiumResponse?.isSuccessResponse === false)) {
          setShowFailedUpdateError(true);
          return;
        }

        setUpdateSuccess(true);
      } else {
        const premiumResponse = await updatePremiumPaymentDetails({
          data: {
            ...directDebitPayload,
            PolicyNumber: activePet.PolicyNumber
          },
          policyNumber: legacyPolicyNumberHandler(activePet.PolicyNumber),
          isPetsureUnderwriter
        }).unwrap();

        if (premiumResponse?.isSuccessResponse === false) {
          setShowFailedUpdateError(true);
          return;
        }

        setUpdateSuccess(true);
      }
    } catch {
      setShowFailedUpdateError(true);
    }
  };

  return {
    showFailedUpdateError,
    showSelectAllCheckbox,
    returnUrlState,
    paymentMethod,
    partner,
    fieldFormContext,
    formUrl,
    supplierBusinessCode,
    partnerPrefix,
    ddRef,
    soleAuthorityTooltip,
    handleClose,
    handlePaymentMethodChange,
    handleUpdateDirectDebit,
    handlePaymentSuccess
  };
};
